import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiUsers, FiTrendingUp, FiShield, FiTarget, FiBarChart, FiZap, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import './B2BSuccessAgent.css';

const B2BSuccessAgent = () => {
  const features = [
    {
      icon: <FiUsers />,
      title: "Account-level Monitoring",
      description: "Monitor customer accounts with granular insights and proactive issue detection."
    },
    {
      icon: <FiTrendingUp />,
      title: "Anomaly Detection",
      description: "AI-powered anomaly detection for account-level performance and usage patterns."
    },
    {
      icon: <FiShield />,
      title: "Proactive Resolution",
      description: "Automatically detect and resolve issues before they impact customer experience."
    },
    {
      icon: <FiTarget />,
      title: "Customer Success Insights",
      description: "Get actionable insights to improve customer success and reduce churn."
    },
    {
      icon: <FiBarChart />,
      title: "Usage Analytics",
      description: "Comprehensive usage analytics to understand customer behavior and needs."
    },
    {
      icon: <FiZap />,
      title: "Real-time Alerts",
      description: "Instant alerts for account-level issues and performance degradation."
    }
  ];

  const benefits = [
    "Account-level anomaly detection and monitoring",
    "Proactive issue resolution for customer success",
    "Real-time alerts for account performance issues",
    "Customer usage analytics and insights",
    "Automated health checks and reporting",
    "Integration with customer success tools"
  ];

  return (
    <div className="b2b-success-agent-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>B2BSuccessAgent</h1>
            <p className="hero-subtitle">
              For account-level anomaly detection and proactive issue resolution
            </p>
            <div className="hero-actions">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="btn btn-primary">
                  Start Free Trial
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/demo" className="btn btn-outline">
                  Request Demo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Customer Success Monitoring</h2>
            <p>Monitor customer accounts with AI-powered anomaly detection and proactive resolution</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose Our B2BSuccessAgent?</h2>
              <p>
                Our B2B Success Agent provides account-level monitoring with AI-powered 
                anomaly detection for proactive customer success management.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>Success Dashboard</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiUsers />
                    <span>Active Accounts: 1,247</span>
                  </div>
                  <div className="metric">
                    <FiTrendingUp />
                    <span>Health Score: 94%</span>
                  </div>
                  <div className="metric">
                    <FiAlertTriangle />
                    <span>Issues Resolved: 23</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Improve Customer Success?</h2>
            <p>Get account-level monitoring with AI-powered anomaly detection and proactive resolution.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default B2BSuccessAgent;
